// 2FA management actions for the backoffice. We talk to Cognito directly
// instead of going through the legacy /su/auth/2fa-* endpoints because the
// pool is the source of truth for MFA — flipping accounts.totp_enabled in
// the DB had no effect on Cognito's SetUserMFAPreference, so users who
// "enabled" 2FA via the legacy endpoints were never actually challenged on
// their next Cognito login.
//
// Recovery codes (which the legacy backend generated) don't exist on the
// Cognito side. Lockout recovery is handled by the existing /reset-2fa
// email flow rather than per-user codes.
import * as CognitoAuthService from '../../services/CognitoAuthService';

const APP_NAME = 'Betterfield';

// Build the otpauth:// URI consumed by authenticator apps. Cognito's
// associateSoftwareToken returns just the base32 secret; the caller (and
// the QR code) needs the full URI with issuer + account label.
function buildOtpauthUri(secret, username) {
  const label = encodeURIComponent(`${APP_NAME}:${username}`);
  const issuer = encodeURIComponent(APP_NAME);
  return `otpauth://totp/${label}?secret=${secret}&issuer=${issuer}&algorithm=SHA1&digits=6&period=30`;
}

export function setup2FA(onSuccess, onFailure) {
  return async () => {
    try {
      const { secretCode, username } = await CognitoAuthService.setupTOTP();
      onSuccess({
        secret: secretCode,
        otpauthUri: buildOtpauthUri(secretCode, username),
      });
    } catch (err) {
      if (onFailure) onFailure(err);
    }
  };
}

export function enable2FA(token, onSuccess, onFailure) {
  return async () => {
    try {
      await CognitoAuthService.verifyTOTP(token);
      onSuccess();
    } catch (err) {
      if (onFailure) onFailure(err);
    }
  };
}

export function disable2FA(_password, onSuccess, onFailure) {
  // The password argument is kept in the signature for backward
  // compatibility with the dialog UI but is unused: Cognito's
  // setUserMfaPreference operates on the active session and does not
  // require re-authentication.
  return async () => {
    try {
      await CognitoAuthService.disableTOTP();
      onSuccess();
    } catch (err) {
      if (onFailure) onFailure(err);
    }
  };
}

export function get2FAStatus(onSuccess, onFailure) {
  return async () => {
    try {
      const { enabled } = await CognitoAuthService.getTOTPStatus();
      onSuccess({
        enabled,
        // Cognito doesn't expose when MFA was enabled or how many recovery
        // codes remain — the dialog reads these fields and tolerates null.
        enabledAt: null,
        remainingRecoveryCodes: 0,
      });
    } catch (err) {
      if (onFailure) onFailure(err);
    }
  };
}
